import * as React from "react";
import { useEffect, useState } from "react";

import { cn } from "@/utils/utils";

interface NumberInputProps extends Omit<React.ComponentProps<"input">, "value" | "defaultValue" | "onChange"> {
  value?: string | number;
  defaultValue?: string | number;
  alwaysZero?: boolean;
  onChange?: (
    e: Omit<React.ChangeEvent<HTMLInputElement>, "target"> & {
      target: Omit<EventTarget & HTMLInputElement, "value"> & { value: number | undefined };
    }
  ) => void;
}

const Input = React.forwardRef<HTMLInputElement, React.ComponentProps<"input">>(({ className, type, ...props }, ref) => {
  return (
    <input
      type={type}
      className={cn(
        "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-xs transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        className
      )}
      ref={ref}
      {...props}
    />
  );
});
Input.displayName = "Input";

/**
 * Common base for number input components
 */
const createNumberInput = (parseFunc: (value: string) => number, preventKeys?: string[]) => {
  const comp = React.forwardRef<HTMLInputElement, NumberInputProps>(({ alwaysZero, onChange, ...props }, ref) => {
    const [isFocused, setIsFocused] = useState(false);
    const [internalValue, setInternalValue] = useState(() => {
      if (props.value !== undefined) return String(props.value);
      if (props.defaultValue !== undefined) return String(props.defaultValue);
      return "";
    });

    // Sync with external value changes when not focused
    useEffect(() => {
      if (!isFocused && props.value !== undefined) {
        setInternalValue(String(props.value));
      }
    }, [props.value, isFocused]);

    return (
      <Input
        type="number"
        ref={ref}
        {...props}
        value={internalValue}
        onKeyDown={(e) => {
          // Block explicitly prevented keys
          if (preventKeys?.includes(e.key)) {
            e.preventDefault();
            return;
          }
          // If using integer input, block any non-digit character
          if (parseFunc === Number.parseInt && e.key.length === 1 && !/\d/.test(e.key)) {
            e.preventDefault();
            return;
          }
          props.onKeyDown?.(e);
        }}
        onFocus={(e) => {
          setIsFocused(true);
          props.onFocus?.(e);
        }}
        onBlur={(e) => {
          setIsFocused(false);
          const value = e.target.value;

          // Handle empty value
          if (value === "") {
            if (alwaysZero) {
              // If alwaysZero is true, set value to '0' and call onChange with 0
              setInternalValue("0");
              onChange?.({ ...e, target: { ...e.target, value: 0 } });
            } else {
              // If alwaysZero is false/undefined, leave value empty and call onChange with undefined
              setInternalValue("");
              onChange?.({ ...e, target: { ...e.target, value: undefined } });
            }
            return;
          }

          // Handle invalid number input
          const parsedValue = parseFunc(value);
          if (Number.isNaN(parsedValue)) {
            if (alwaysZero) {
              setInternalValue("0");
              onChange?.({ ...e, target: { ...e.target, value: 0 } });
            } else {
              setInternalValue("");
              onChange?.({ ...e, target: { ...e.target, value: undefined } });
            }
            return;
          }

          if (props.min !== undefined && parsedValue < Number(props.min)) {
            setInternalValue(String(props.min));
            onChange?.({ ...e, target: { ...e.target, value: Number(props.min) } });
            return;
          }

          // Handle valid number
          setInternalValue(String(parsedValue));
          onChange?.({ ...e, target: { ...e.target, value: parsedValue } });

          props.onBlur?.(e);
        }}
        onChange={(e) => {
          // During typing, just update the internal value without validation
          const value = e.target.value;
          setInternalValue(value);
        }}
        onInput={(e) => {
          const target = e.target as HTMLInputElement;
          const raw = target.value;
          setInternalValue(raw);
          const parsed = parseFunc(raw);
          // Only propagate valid numbers
          if (raw.trim() !== "" && !Number.isNaN(parsed)) {
            onChange?.({ ...e, target: { ...target, value: parsed } });
          }
        }}
      />
    );
  });
  comp.displayName = "NumberInput";
  return comp;
};

// Integer input component
const IntInput = createNumberInput((value) => Number.parseInt(value, 10), [".", ","]);
IntInput.displayName = "IntInput";

// Float input component
const FloatInput = createNumberInput(Number.parseFloat);
FloatInput.displayName = "FloatInput";

export { FloatInput, Input, IntInput };
